import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

type GtagCommand = 'js' | 'config' | 'event'
type GtagFn = (command: GtagCommand, ...args: unknown[]) => void

interface WindowWithGtag {
  gtag?: GtagFn
}

function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null

  const gtag = (window as WindowWithGtag).gtag
  return typeof gtag === 'function' ? gtag : null
}

function detectAIReferralSource(referrer: string): string {
  const normalizedReferrer = referrer.toLowerCase()

  if (normalizedReferrer.includes('chat.openai.com') || normalizedReferrer.includes('chatgpt.com')) {
    return 'chatgpt'
  }

  if (normalizedReferrer.includes('perplexity.ai')) {
    return 'perplexity'
  }

  if (normalizedReferrer.includes('gemini.google.com')) {
    return 'gemini'
  }

  if (normalizedReferrer.includes('copilot.microsoft.com')) {
    return 'copilot'
  }

  if (normalizedReferrer.includes('claude.ai')) {
    return 'claude'
  }

  return 'unknown-ai'
}

function resolveCalculatorSlug(route: RouteLocationNormalizedLoaded): string {
  const slugParam = route.params.slug
  if (typeof slugParam === 'string' && slugParam.length > 0) {
    return slugParam
  }

  const pathSegments = route.path.split('/').filter(Boolean)
  if (pathSegments.length > 0) {
    return pathSegments[pathSegments.length - 1]
  }

  if (typeof route.name === 'string') {
    return route.name
  }

  return 'unknown-calculator'
}

/**
 * Track AI referral sessions
 * Captures LLM referrals from ChatGPT, Perplexity, Gemini, and Copilot.
 *
 * NOTE: This captures a partial picture only. Privacy modes and cross-domain
 * navigation can suppress referrer data.
 */
function trackAIReferral(calculatorSlug: string): void {
  const gtag = getGtag()
  if (!gtag || typeof document === 'undefined') return

  const referrer = document.referrer || ''
  const source = detectAIReferralSource(referrer)

  gtag('event', 'ai_referral_session', {
    referral_source: source,
    landing_calculator: calculatorSlug,
    referrer_full: referrer.substring(0, 100) || 'none'
  })
}

/**
 * Initialize AI referral tracking on route navigation.
 */
export function initAIReferralTracking(router: Router): void {
  router.afterEach((to) => {
    const isCalculatorRoute =
      to.path.includes('/calculators/') ||
      to.path === '/steel-column' ||
      (typeof to.name === 'string' && to.name.includes('calculator'))

    if (!isCalculatorRoute) {
      return
    }

    const calculatorSlug = resolveCalculatorSlug(to)
    trackAIReferral(calculatorSlug)
  })
}

// ============================================================================
// Sprint 2 — Acquisition Funnel Analytics
// All functions use gtag() via getGtag() — no direct window.gtag calls.
// ============================================================================

export function trackCalculatorView(calculatorSlug: string): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'calculator_view', { calculator_slug: calculatorSlug })
}

export function trackCalculationPerformed(calculatorSlug: string, durationMs: number): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'calculation_performed', { calculator_slug: calculatorSlug, duration_ms: durationMs })
}

export function trackCTAShown(calculatorSlug: string, ctaPosition?: string): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'cta_shown', { calculator_slug: calculatorSlug, cta_position: ctaPosition ?? 'post_calc' })
}


export function trackLeadModalOpen(calculatorSlug: string): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'lead_modal_open', { calculator_slug: calculatorSlug })
}

export function trackLeadSubmit(calculatorSlug: string): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'lead_submit', { calculator_slug: calculatorSlug })
}

export function trackSignup(calculatorSlug: string): void {
  const gtag = getGtag()
  if (!gtag) return
  gtag('event', 'signup', { calculator_slug: calculatorSlug })
}
